import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BadgeCheck, MessageCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { whatsappAPI } from '../../services/api';

// Phone-only header shared by the two WhatsApp pages (Official = Kapso, Unofficial = self-hosted gateway):
// a two-way switch so you always know which one you are managing, plus a one-line status strip.
// Hidden on desktop through CSS (.wa-switch is shown only inside .app-view, see appView.css).
// Read-only: it only calls the status endpoints the pages already use.
const dot = (color) => <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />;
const GREEN = 'var(--c-0ca30c, #0ca30c)';
const AMBER = 'var(--cb-f59e0b, #f59e0b)';
const RED = 'var(--danger, #dc2626)';

function useGatewayStrip(enabled) {
  const [s, setS] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const load = () => Promise.all([
      whatsappAPI.getStatus().then(({ data }) => data.data).catch(() => ({ status: 'unreachable' })),
      whatsappAPI.getQueueStatus().then(({ data }) => data.data).catch(() => null),
    ]).then(([status, queue]) => alive && setS({ status, queue }));
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [enabled]);
  return s;
}

// numbers = the Kapso numbers the page already loaded (null while loading).
export default function WaSwitch({ active, numbers }) {
  const navigate = useNavigate();
  const { isAdmin, hasModuleRight } = useAuth();
  const canOfficial = isAdmin || hasModuleRight('kapso');
  const canGateway = isAdmin || hasModuleRight('whatsappGateway');
  const gw = useGatewayStrip(active === 'unofficial');

  let strip = null;
  if (active === 'unofficial') {
    const st = gw?.status?.status;
    const label = { connected: 'Connected', connecting: 'Connecting…', qr: 'Scan QR to link', disconnected: 'Not connected', unreachable: 'Gateway unreachable' }[st] || 'Checking…';
    const color = st === 'connected' ? GREEN : st === 'unreachable' || st === 'disconnected' ? RED : AMBER;
    const q = gw?.queue;
    strip = (
      <>
        {dot(color)}<b>{label}</b>
        {gw?.status?.phoneNumber && <span>{gw.status.phoneNumber}</span>}
        {q && <span style={{ marginLeft: 'auto' }}>Today {q.sentToday}/{q.dailyLimit}</span>}
      </>
    );
  } else {
    const n = (numbers || []).find((x) => x.kind !== 'sandbox') || (numbers || [])[0];
    const ok = n && n.status === 'CONNECTED' && (!n.health || n.health.status === 'healthy');
    strip = numbers === null ? <b>Checking…</b> : !n ? <>{dot(RED)}<b>No number linked</b></> : (
      <>
        {dot(ok ? GREEN : AMBER)}<b>{ok ? 'Connected' : (n.status || 'Check number')}</b>
        <span>{n.display_phone_number || 'Sandbox'}</span>
        {n.health && <span style={{ marginLeft: 'auto' }}>Health: {n.health.status}</span>}
      </>
    );
  }

  const seg = (key, path, label, Icon, allowed) => allowed && (
    <button type="button" key={key} onClick={() => active !== key && navigate(path)} aria-pressed={active === key}
      className={active === key ? 'wa-seg wa-seg-on' : 'wa-seg'}>
      <Icon size={16} /> {label}
    </button>
  );

  return (
    <div className="wa-switch">
      <div className="wa-segs">
        {seg('official', '/kapso', 'Official', BadgeCheck, canOfficial)}
        {seg('unofficial', '/whatsapp-api', 'Unofficial', MessageCircle, canGateway)}
      </div>
      <div className="wa-strip">{strip}</div>
    </div>
  );
}
